import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { MarketplaceKey } from "@porsche-customer/slfinrtl-context-types"
import { currentEnvironment } from "@porsche-customer/slfinrtl-environment"
import axios from "axios"
import { useCallback, useEffect, useState } from "react"

export const localStorageKey = "capSamlToken"
const expiryOffset = 3600_000 // 1 hour
const refreshInterval = 1800_000 // 30 minutes

export const useCapSamlToken = (onError?: (error: Error) => void, enabled = true) => {
	const { marketplaceKey, username } = useDealerContext()
	const [samlToken, setSamlToken] = useState(() => getCachedSamlToken(username))

	const updateSamlToken = useCallback(async () => {
		if (!samlToken || !isSamlTokenValid(samlToken, username)) {
			try {
				const newSamlToken = await fetchSamlToken(marketplaceKey)
				if (!isSamlTokenValid(newSamlToken, username)) {
					throw new Error("Fetched SAML token is invalid for the current user")
				}
				localStorage.setItem(localStorageKey, newSamlToken)
				setSamlToken(newSamlToken)
			} catch (error) {
				if (onError) {
					onError(error as Error)
				} else {
					console.error("Error fetching SAML token", error)
				}
			}
		}
	}, [marketplaceKey, onError, samlToken, username])

	useEffect(() => {
		if (!enabled) {
			return
		}
		// eslint-disable-next-line react-hooks/set-state-in-effect
		updateSamlToken()
		const refreshTimer = setInterval(() => {
			updateSamlToken()
		}, refreshInterval)
		return () => {
			clearInterval(refreshTimer)
		}
	}, [enabled, updateSamlToken])

	return samlToken
}

const isMockedCapIntegration = () => {
	// due to issues with different PPN stages, the preview environment
	// is not actually connected to C@P and doesn't need a real SAML token
	return currentEnvironment() === "preview"
}

const isSamlTokenValid = (samlTokenBase64: string, username: string) => {
	if (isMockedCapIntegration()) {
		return true
	}
	try {
		const samlXML = atob(samlTokenBase64)
		const samlToken = new DOMParser().parseFromString(samlXML, "text/xml")

		return hasMatchingUsername(samlToken, username) && !isExpired(samlToken)
	} catch (e) {
		console.error("Could not parse SAML token", e)
		return false
	}
}

const hasMatchingUsername = (samlTokenDocument: Document, username: string) => {
	const tokenUsername = [...samlTokenDocument.getElementsByTagName("saml2:Attribute")]
		.find((node) => node.getAttribute("Name") === "ppn_username")
		?.getElementsByTagName("saml2:AttributeValue")[0]
		?.textContent?.trim()
	return tokenUsername === username
}

const isExpired = (samlTokenDocument: Document) => {
	const expiryTimestamp = [...samlTokenDocument.getElementsByTagName("saml2:Conditions")]
		.find((node) => node.hasAttribute("NotOnOrAfter"))
		?.getAttribute("NotOnOrAfter")
	const expiryDateMillis = expiryTimestamp ? new Date(expiryTimestamp).getTime() : Date.now()
	// token is considered expired if it will expire within the next hour
	return expiryDateMillis < Date.now() + expiryOffset
}

const getCachedSamlToken = (username: string) => {
	const samlTokenBase64 = localStorage.getItem(localStorageKey)
	if (!samlTokenBase64) {
		return undefined
	}
	return isSamlTokenValid(samlTokenBase64, username) ? samlTokenBase64 : undefined
}

const fetchSamlToken = async (marketplaceKey: MarketplaceKey) => {
	if (isMockedCapIntegration()) {
		return "mockCapSamlToken"
	}

	const environment = currentEnvironment()
	const capRegion = getCapRegionFromMarketplace(marketplaceKey)
	const url = capRegionUrlMap[capRegion][environment === "prod" ? "prod" : "dev"]

	const response = await axios.get(url, {
		withCredentials: true,
	})
	return extractSAMLTokenFromHtml(response.data)
}

const extractSAMLTokenFromHtml = (responseBody: string): string => {
	const regex = /<input type="hidden" name="SAMLResponse" value="(?<samlToken>.*?)"\/>/
	const match = regex.exec(responseBody)
	if (match?.groups) {
		return match.groups.samlToken
	}
	throw new Error("ExtractSAMLTokenFromHtml: SAML Token could not be extracted from ShadowApp response")
}

type CapEnvironment = "prod" | "dev"

enum CapRegion {
	AMERICA = "america",
	ASIA = "asia",
	EUROPE = "europe",
}

const capRegionUrlMap: Record<CapRegion, Record<CapEnvironment, string>> = {
	[CapRegion.AMERICA]: {
		dev: "https://ppnlite.porsche.com/ppnmda/appforward.do?cn=CRM_Porsche_KV1&sso=SS_APIGW",
		prod: "https://ppn.porsche.com/ppnmda/appforward.do?cn=CRM_Porsche_PV1&sso=SS_APIGW",
	},
	[CapRegion.ASIA]: {
		dev: "https://ppnlite.porsche.com/ppnmda/appforward.do?cn=CRM_KV4_001&sso=SS_APIGW",
		prod: "https://ppn.porsche.com/ppnmda/appforward.do?cn=CRM_PV4&sso=SS_APIGW",
	},
	[CapRegion.EUROPE]: {
		dev: "https://ppnlite.porsche.com/ppnmda/appforward.do?cn=CRM_Porsche_KV3&sso=SS_APIGW",
		prod: "https://ppn.porsche.com/ppnmda/appforward.do?cn=CRM_SSO&sso=SS_APIGW",
	},
}

const capRegions: Record<MarketplaceKey, CapRegion | undefined> = {
	am: CapRegion.EUROPE,
	ar: CapRegion.AMERICA,
	at: CapRegion.EUROPE,
	au: CapRegion.ASIA,
	az: CapRegion.ASIA,
	ba: CapRegion.EUROPE,
	be: CapRegion.EUROPE,
	bg: CapRegion.EUROPE,
	bh: CapRegion.ASIA,
	bn: CapRegion.ASIA,
	bo: CapRegion.AMERICA,
	br: CapRegion.AMERICA,
	ca: CapRegion.AMERICA,
	ch: CapRegion.EUROPE,
	cl: CapRegion.AMERICA,
	cn: undefined, // China has its own C@P instance, we don't deploy our feature there live
	co: CapRegion.AMERICA,
	cr: CapRegion.AMERICA,
	cw: CapRegion.AMERICA,
	cy: CapRegion.EUROPE,
	cz: CapRegion.EUROPE,
	de: CapRegion.EUROPE,
	dk: CapRegion.EUROPE,
	do: CapRegion.AMERICA,
	ec: CapRegion.AMERICA,
	ee: CapRegion.EUROPE,
	eg: CapRegion.ASIA,
	es: CapRegion.EUROPE,
	fi: CapRegion.EUROPE,
	fr: CapRegion.EUROPE,
	gb: CapRegion.EUROPE,
	ge: CapRegion.EUROPE,
	gp: CapRegion.AMERICA,
	gr: CapRegion.EUROPE,
	gt: CapRegion.AMERICA,
	hk: CapRegion.ASIA,
	hn: CapRegion.AMERICA,
	hr: CapRegion.EUROPE,
	hu: CapRegion.EUROPE,
	id: CapRegion.ASIA,
	ie: CapRegion.EUROPE,
	il: CapRegion.EUROPE,
	in: CapRegion.ASIA,
	is: CapRegion.EUROPE,
	it: CapRegion.EUROPE,
	jm: CapRegion.AMERICA,
	jo: CapRegion.ASIA,
	jp: CapRegion.ASIA,
	kh: CapRegion.ASIA,
	kr: CapRegion.ASIA,
	kw: CapRegion.ASIA,
	kz: CapRegion.EUROPE,
	lb: CapRegion.ASIA,
	lk: CapRegion.ASIA,
	lt: CapRegion.EUROPE,
	lu: CapRegion.EUROPE,
	lv: CapRegion.EUROPE,
	ma: CapRegion.ASIA,
	md: CapRegion.EUROPE,
	mk: CapRegion.EUROPE,
	mn: CapRegion.ASIA,
	mt: CapRegion.EUROPE,
	mu: CapRegion.ASIA,
	mx: CapRegion.AMERICA,
	my: CapRegion.ASIA,
	nc: CapRegion.ASIA,
	nl: CapRegion.EUROPE,
	no: CapRegion.EUROPE,
	nz: CapRegion.ASIA,
	om: CapRegion.ASIA,
	pa: CapRegion.AMERICA,
	pe: CapRegion.AMERICA,
	pf: CapRegion.ASIA,
	ph: CapRegion.ASIA,
	pl: CapRegion.EUROPE,
	pr: CapRegion.AMERICA,
	pt: CapRegion.EUROPE,
	py: CapRegion.AMERICA,
	qa: CapRegion.ASIA,
	re: CapRegion.ASIA,
	ro: CapRegion.EUROPE,
	rs: CapRegion.EUROPE,
	sa: CapRegion.ASIA,
	sg: CapRegion.ASIA,
	si: CapRegion.EUROPE,
	sk: CapRegion.EUROPE,
	sv: CapRegion.AMERICA,
	th: CapRegion.ASIA,
	tn: CapRegion.ASIA,
	tr: CapRegion.EUROPE,
	tt: CapRegion.AMERICA,
	tw: CapRegion.ASIA,
	ua: CapRegion.EUROPE,
	us: CapRegion.AMERICA,
	uy: CapRegion.AMERICA,
	uz: CapRegion.EUROPE,
	vn: CapRegion.ASIA,
	xa: CapRegion.ASIA,
	xd: CapRegion.ASIA,
	xp: CapRegion.EUROPE,
	za: CapRegion.ASIA,
}

const getCapRegionFromMarketplace = (marketplaceKey: MarketplaceKey) => {
	const region = capRegions[marketplaceKey]
	if (!region) {
		throw new Error(`No region configured for marketplace ${marketplaceKey}`)
	}
	return region
}
